import { ArrowUpNarrowWide, ArrowDownNarrowWide } from "lucide-react";
import { Button } from "snap-design-system";
import { cn } from "~/lib/utils";
import {
  REPORT_LIST_DEFAULT_ORDER,
  REPORT_LIST_DEFAULT_COLUMN_ORDER,
} from "~/helpers/constants";

interface SortToggleGroupProps {
  columnOrder: "created_at" | "modified_at";
  order: "asc" | "desc";
  onColumnOrderToggle: () => void;
  onOrderToggle: () => void;
}

const columnOrderLabels: Record<SortToggleGroupProps["columnOrder"], string> = {
  created_at: "Data Criação",
  modified_at: "Data Edição",
};

/**
 * A cohesive toggle group that combines column-order and sort-direction controls.
 * Uses outlined Button components from snap-design-system styled as a toggle group.
 * Text turns accent when columnOrder is non-default; icon turns accent when order is non-default.
 */
export function SortToggleGroup({
  columnOrder,
  order,
  onColumnOrderToggle,
  onOrderToggle,
}: SortToggleGroupProps) {
  const isAsc = order === "asc";
  const isColumnOrderNonDefault = columnOrder !== REPORT_LIST_DEFAULT_COLUMN_ORDER;
  const isOrderNonDefault = order !== REPORT_LIST_DEFAULT_ORDER;

  return (
    <div className="inline-flex" role="group">
      <Button
        variant="ghost"
        onClick={onColumnOrderToggle}
        className={cn(
          "w-full md:w-36 rounded-r-none border border-input",
          isColumnOrderNonDefault && "text-accent-500",
        )}
        aria-label={`Ordenar por ${columnOrderLabels[columnOrder]}. Clique para alternar.`}
        title={`Ordenar por ${columnOrderLabels[columnOrder]}`}
        data-test-id="snap-reports-sort-field-toggle"
      >
        {columnOrderLabels[columnOrder]}
      </Button>

      <Button
        variant="ghost"
        onClick={onOrderToggle}
        className={cn(
          "rounded-l-none border border-l-0 border-input",
          isOrderNonDefault && "text-accent-500",
        )}
        aria-label={isAsc ? "Ordem ascendente. Clique para descendente." : "Ordem descendente. Clique para ascendente."}
        title={isAsc ? "Mais antigos primeiro" : "Mais recentes primeiro"}
        data-test-id="snap-reports-sort-direction-toggle"
      >
        {isAsc ? <ArrowUpNarrowWide size={24} /> : <ArrowDownNarrowWide size={24} />}
      </Button>
    </div>
  );
}
